import React, { useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useKeycloak } from '@react-keycloak/web';
import { komprimiereBild } from '../bildKompressor';
import { apiFetch } from '../portal/ApiClient';

/**
 * Berechtigungsgeber-Wechsel: die ZÜP des Mitglieds läuft bei einem anderen
 * Verein/Arbeitgeber und soll zum FZT übertragen werden. KEINE Neuanmeldung
 * (die wäre eine verbotene Doppel-Anlage) — stattdessen Personenzuordnung
 * über das Ministerium (BMIMI) per Passkopie.
 */
function BgWechsel({ goBack }: { goBack: () => void }) {
  const { keycloak } = useKeycloak();
  const [alterBg, setAlterBg] = useState('');
  const [datei, setDatei] = useState<File | null>(null);
  const [dateiInfo, setDateiInfo] = useState('');
  const [bestaetigt, setBestaetigt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);

  const handleDatei = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setDateiInfo('');
    setDatei(null);
    if (!f) return;
    const r = await komprimiereBild(f);
    if (r.problem) {
      setDateiInfo('⚠️ ' + r.problem);
      return;
    }
    if (r.verkleinert) {
      setDateiInfo(`✅ Foto automatisch verkleinert: ${(f.size / 1e6).toFixed(1)} MB → ${(r.file.size / 1e6).toFixed(1)} MB`);
    }
    setDatei(r.file);
  };

  const absenden = async () => {
    if (!datei || !alterBg.trim() || !bestaetigt) return;
    setBusy(true);
    setErgebnis(null);
    try {
      await apiFetch('/api/me/bg-wechsel', keycloak.token, {
        method: 'POST',
        headers: {
          'Content-Type': datei.type,
          'X-Alter-Bg': encodeURIComponent(alterBg.trim()),
          'X-Filename': encodeURIComponent(datei.name),
          'X-Bestaetigung': 'richtig',
        },
        body: datei,
      });
      setErgebnis({
        variant: 'success',
        text: 'Danke! Dein Antrag ist bei uns eingegangen. Wir reichen deine Passkopie beim '
          + 'Ministerium ein und melden uns, sobald die Zuordnung erledigt ist — du brauchst '
          + 'vorerst nichts weiter zu tun.',
      });
    } catch (err) {
      setErgebnis({ variant: 'danger', text: 'Einreichen fehlgeschlagen: ' + (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container fluid='md' className='py-4' style={{ maxWidth: 720 }}>
      <Button variant='link' className='px-0 mb-2' onClick={goBack}>← Zurück</Button>
      <h2 className='mb-3'>ZÜP zu uns übertragen</h2>

      <Card className='mb-4'>
        <Card.Body>
          <Card.Title as='h5'>So läuft der Wechsel ab</Card.Title>
          <ol className='mb-2' style={{ fontSize: '0.95rem' }}>
            <li>Du gibst hier deinen bisherigen Berechtigungsgeber an und lädst eine gut lesbare Passkopie hoch</li>
            <li>Wir reichen die Zuordnung beim Ministerium (BMIMI) ein</li>
            <li>Das Ministerium überträgt deine ZÜP-Verwaltung auf das Flugsportzentrum Tirol — meist innerhalb weniger Tage</li>
            <li>Danach bitten wir dich, deine Daten (Wohnsitz, Beschäftigung) kurz im Portal zu aktualisieren</li>
            <li>Ist deine ZÜP noch gültig, läuft sie einfach bei uns weiter — war sie bereits abgelaufen, geben wir nach deinem Daten-Update die behördliche Prüfung neu frei (die Behörde braucht dafür bis zu 4 Wochen)</li>
          </ol>
          <div className='text-muted' style={{ fontSize: '0.85rem' }}>
            Wichtig: Der Wechsel funktioniert auch, wenn deine ZÜP schon abgelaufen ist —
            wir stellen sie dann über uns wieder her. Eine noch gültige ZÜP bleibt während
            des Wechsels aufrecht. In beiden Fällen gilt: Übertragung statt Neuanmeldung,
            eine Doppel-Anmeldung ist nicht zulässig.
          </div>
        </Card.Body>
      </Card>

      {ergebnis && <Alert variant={ergebnis.variant}>{ergebnis.text}</Alert>}

      {!ergebnis?.variant?.includes('success') && (
        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>Bisheriger Berechtigungsgeber (Verein / Arbeitgeber)</Form.Label>
            <Form.Control
              type='text'
              placeholder='z. B. ESV, Austrian Airlines, anderer Flugverein'
              value={alterBg}
              onChange={(e) => setAlterBg(e.target.value)}
            />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Label>Passkopie (Foto oder PDF, gut lesbar)</Form.Label>
            <Form.Control type='file' accept='image/*,application/pdf' onChange={handleDatei} />
            {dateiInfo && <Form.Text className={dateiInfo.startsWith('⚠️') ? 'text-danger' : 'text-success'}>{dateiInfo}</Form.Text>}
          </Form.Group>
          <Form.Check
            className='mb-3'
            id='bg-bestaetigung'
            label='Ich bestätige, dass die Angaben richtig sind und ich die Übertragung meiner ZÜP-Verwaltung zum Flugsportzentrum Tirol beauftrage.'
            checked={bestaetigt}
            onChange={(e) => setBestaetigt(e.target.checked)}
          />
          <Button disabled={busy || !datei || !alterBg.trim() || !bestaetigt} onClick={absenden}>
            {busy ? <Spinner size='sm' animation='border' /> : 'Übertragung beauftragen'}
          </Button>
        </Form>
      )}
    </Container>
  );
}

export default BgWechsel;
